import {
  Dropdown,
  DropdownItem,
  DropdownItemProps,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  extendVariants,
} from "@heroui/react";
import { ActiveRadioIcon, InactiveRadioIcon } from "components/Combobox";
import { Radio } from "design_system";
import { ReactNode } from "react";
const CoDropdown = extendVariants(Dropdown, {
  variants: {
    variant: {
      solid: {
        base: "border border-border-base before:bg-bg-base",
      },
    },
  },
  defaultVariants: {
    variant: "solid",
  },
});

const CoDropdownItem = extendVariants(DropdownItem, {
  variants: {
    variant: {
      solid: {
        base: [
          "p-2 data-hover:bg-bg-faint data-hover:text-fg-base gap-3 transition-colors",
        ],
        title: "!tiny-md",
        description: "tiny-normal text-fg-muted",
      },
      footer: {
        base: ["p-0 gap-3"],
        title: "tiny-md",
        description: "tiny-normal text-fg-muted",
      },
    },
    danger: {
      true: {
        base: "text-alert-fg-base data-hover:bg-chart-red-faint data-hover:text-alert-fg-base",
      },
    },
  },
  defaultVariants: {
    variant: "solid",
    color: "default",
    danger: false,
  },
});

const CoDropdownMenu = extendVariants(DropdownMenu, {
  variants: {
    variant: {
      solid: {
        base: ["p-0"],
      },
    },
  },
  defaultVariants: {
    variant: "solid",
    color: "default",
  },
});

const CoDropdownSection = extendVariants(DropdownSection, {
  variants: {
    variant: {
      solid: {
        base: ["m-0"],
        heading: ["tiny-md text-fg-muted px-2 py-1 block"],
      },
      footer: {
        base: ["m-0 px-2 pt-2 pb-1"],
        heading: ["tiny-md text-fg-muted px-2 py-1 block"],
      },
    },
  },
  defaultVariants: {
    variant: "solid",
    color: "default",
  },
});

const renderDropdownItem = (
  item: CoDropdownItemProps,
  useRadioIndicators: boolean,
  selectedKey?: string | number,
  disabledKeys?: (string | number)[]
) => {
  const { key, leftIcon, rightIcon, danger, ...theRest } = item;

  const radioIcon = useRadioIndicators ? (
    <Radio isChecked={selectedKey === key} />
  ) : (
    rightIcon
  );

  // Pass all props to the dropdown item except children, title, and description
  // which will be custom handled in this component
  let restProps = {
    ...theRest,
  };
  delete restProps.children;
  delete restProps.description;
  delete restProps.title;

  return (
    <CoDropdownItem
      key={key}
      isDisabled={disabledKeys?.includes(key)}
      startContent={leftIcon}
      endContent={useRadioIndicators ? radioIcon : rightIcon}
      danger={danger}
      data-testid={item.dataTestId}
      {...restProps}
    >
      <div className="flex flex-col max-w-[212px]">
        <div className="flex gap-2 items-center">
          {item.title && <span className="text-left">{item.title}</span>}
          {item.tag}
        </div>
        {item.description && (
          <span className="text-xs font-normal text-left text-fg-muted">
            {item.description}
          </span>
        )}
      </div>
    </CoDropdownItem>
  );
};

export { DropdownTrigger, useDropdown } from "@heroui/dropdown";
export type {
  DropdownItemProps,
  DropdownMenuProps,
  DropdownProps,
  DropdownTriggerProps,
} from "@heroui/dropdown";
export {
  ActiveRadioIcon,
  CoDropdown,
  CoDropdownItem,
  CoDropdownMenu,
  CoDropdownSection,
  InactiveRadioIcon,
};

export type CoDropdownItemProps = {
  onClick?: () => void;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  title?: ReactNode;
  description?: string;
  danger?: boolean;
  dataTestId?: string;
  tag?: ReactNode;
} & DropdownItemProps;

// Placement type for dropdown positioning
type Placement =
  | "top"
  | "bottom"
  | "right"
  | "left"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end";

type CoDropdownPropsType = {
  children: ReactNode;
  disabledKeys?: (string | number)[];
  isDisabled?: boolean;
  items: CoDropdownItemProps[];
  footer?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  showArrow?: boolean;
  title?: string;
  useRadioIndicators?: boolean;
  selectedKey?: string | number;
};

export default function MainDropdown({
  children,
  items,
  footer,
  title = "",
  disabledKeys,
  isDisabled = false,
  onOpenChange,
  placement,
  showArrow,
  useRadioIndicators = false,
  selectedKey,
}: CoDropdownPropsType) {
  return (
    <Dropdown
      radius="md"
      onOpenChange={onOpenChange}
      shouldBlockScroll={false}
      shouldCloseOnScroll={false}
      placement={placement}
      showArrow={showArrow}
      classNames={{
        base: "shadow-none",
        content: "bg-bg-base border shadow-xs",
      }}
      isDisabled={isDisabled}
    >
      <DropdownTrigger>
        <div className="z-1">{children}</div>
      </DropdownTrigger>
      <CoDropdownMenu>
        <CoDropdownSection title={title} showDivider={!!footer}>
          {items.map((item) =>
            renderDropdownItem(
              item,
              useRadioIndicators,
              selectedKey,
              disabledKeys
            )
          )}
        </CoDropdownSection>
        {footer !== undefined ? (
          <CoDropdownSection variant="footer">
            <CoDropdownItem variant="footer">{footer}</CoDropdownItem>
          </CoDropdownSection>
        ) : null}
      </CoDropdownMenu>
    </Dropdown>
  );
}
